import { supabase } from "../supabase/supabaseClient";

const parseStoragePublicUrl = (url) => {
    if (!url || typeof url !== "string") return null;

    const cleanUrl = url.split("?")[0];
    const match = cleanUrl.match(/\/storage\/v1\/object\/public\/([^/]+)\/(.+)$/i);
    if (!match) return null;

    const bucket = decodeURIComponent(match[1] || "");
    const objectPath = decodeURIComponent(match[2] || "");
    if (!bucket || !objectPath) return null;

    return { bucket, objectPath };
};

/**
 * Create a new post
 */
export const createPost = async (authorId, body, visibility = 'public') => {
    return await supabase
        .from("posts")
        .insert([{ 
            author_id: authorId, 
            body,
            visibility
        }])
        .select()
        .single();
};

/**
 * Add media to a post
 */
export const addPostMedia = async (postId, mediaType, url, position = 0) => {
    return await supabase
        .from("post_media")
        .insert([{
            post_id: postId,
            media_type: mediaType,
            url,
            position
        }])
        .select()
        .single();
};

/**
 * Get all posts (for feed)
 */
export const getPosts = async (limit = 20) => {
    return await supabase
        .from("posts")
    .select("id, author_id, body, visibility, created_at, post_media(id, media_type, url, position)")
        .is("deleted_at", null)
        .order("created_at", { ascending: false })
        .limit(limit);
};

/**
 * Get posts by user
 */
export const getPostsByUser = async (authorId) => {
    return await supabase
        .from("posts")
    .select("id, author_id, body, visibility, created_at, post_media(id, media_type, url, position)")
        .eq("author_id", authorId)
        .is("deleted_at", null)
        .order("created_at", { ascending: false });
};

/**
 * Delete a post (soft delete)
 */
export const deletePost = async (postId) => {
    return await supabase
        .from("posts")
        .update({ deleted_at: new Date().toISOString() })
        .eq("id", postId);
};

export const removePostMediaFromStorage = async (mediaItems = []) => {
    const bucketToPaths = new Map();

    (mediaItems || []).forEach((item) => {
        const parsed = parseStoragePublicUrl(item?.url || "");
        if (!parsed) return;

        if (!bucketToPaths.has(parsed.bucket)) {
            bucketToPaths.set(parsed.bucket, new Set());
        }

        bucketToPaths.get(parsed.bucket).add(parsed.objectPath);
    });

    const failures = [];
    let removedCount = 0;

    for (const [bucket, pathSet] of bucketToPaths.entries()) {
        const paths = Array.from(pathSet);
        if (paths.length === 0) continue;

        const { error } = await supabase.storage.from(bucket).remove(paths);
        if (error) {
            failures.push(`${bucket}: ${error.message || "remove failed"}`);
            continue;
        }

        removedCount += paths.length;
    }

    return {
        removedCount,
        failures,
    };
};

export const deletePostWithMediaCleanup = async (postId, mediaItems = []) => {
    const cleanup = await removePostMediaFromStorage(mediaItems);
    const { error } = await deletePost(postId);

    if (error) {
        return { data: null, error, cleanup };
    }

    return { data: { id: postId }, error: null, cleanup };
};

/**
 * Add a reaction to a post
 */
export const addReaction = async (postId, userId, reaction = 'like') => {
    return await supabase
        .from("post_reactions")
        .insert([{ 
            post_id: postId, 
            user_id: userId,
            reaction
        }])
        .select()
        .single();
};

/**
 * Remove a reaction from a post
 */
export const removeReaction = async (postId, userId) => {
    return await supabase
        .from("post_reactions")
        .delete()
        .eq("post_id", postId)
        .eq("user_id", userId);
};

/**
 * Get reactions for a post
 */
export const getReactions = async (postId) => {
    return await supabase
        .from("post_reactions")
        .select("*")
        .eq("post_id", postId);
};

/**
 * Add a comment to a post
 */
export const addComment = async (postId, authorId, body) => {
    return await supabase
        .from("post_comments")
        .insert([{ 
            post_id: postId, 
            author_id: authorId,
            body
        }])
        .select()
        .single();
};

/**
 * Get comments for a post
 */
export const getComments = async (postId) => {
    return await supabase
        .from("post_comments")
        .select("*")
        .eq("post_id", postId)
        .is("deleted_at", null)
        .order("created_at", { ascending: true });
};

/**
 * Delete a comment (soft delete)
 */
export const deleteComment = async (commentId) => {
    return await supabase
        .from("post_comments")
        .update({ deleted_at: new Date().toISOString() })
        .eq("id", commentId);
};
